import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { bonusesAPI, withdrawalsAPI } from "@/services/api";
import { toast } from "sonner";
import { BONUS_TYPES, formatCurrency, formatDate } from "@/utils/constants";
import { getErrorMessage } from "@/utils/errorHelpers";
import { LoadingState, PageHeader, PageShell } from "@/components/app/page";
import { Archive, Clock, Wallet } from "lucide-react";

const STATUS_BADGES = {
  pending: { label: "En attente", className: "bg-amber-100 text-amber-800" },
  processing: { label: "En traitement", className: "bg-blue-100 text-blue-800" },
  paid: { label: "Paye", className: "bg-green-100 text-green-800" },
  rejected: { label: "Rejete", className: "bg-red-100 text-red-800" },
  cancelled: { label: "Annule", className: "bg-slate-100 text-slate-700" },
};

export default function BonusesPage() {
  const [bonuses, setBonuses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [withdrawalSummary, setWithdrawalSummary] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [archives, setArchives] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthKey());
  const [loading, setLoading] = useState(true);

  const loadBonuses = useCallback(async () => {
    try {
      const params = { month: selectedMonth };
      const [bonusesRes, summaryRes, withdrawalSummaryRes, withdrawalsRes, archivesRes] = await Promise.all([
        bonusesAPI.list(params),
        bonusesAPI.getSummary(params),
        withdrawalsAPI.getSummary(),
        withdrawalsAPI.list(),
        bonusesAPI.getMonthlyArchives(),
      ]);
      setBonuses(bonusesRes.data);
      setSummary(summaryRes.data);
      setWithdrawalSummary(withdrawalSummaryRes.data);
      setArchives(Array.isArray(archivesRes.data) ? archivesRes.data : []);
      setWithdrawals(
        Array.isArray(withdrawalsRes.data)
          ? withdrawalsRes.data.filter((withdrawal) => withdrawal.month === selectedMonth)
          : []
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors du chargement des bonus"));
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    loadBonuses();
  }, [loadBonuses]);

  const bonusTypeEntries = useMemo(() => Object.entries(summary?.summary || {}), [summary]);
  const selectedArchive = archives.find((item) => item.month === selectedMonth);
  const currentMonth = selectedMonth === getCurrentMonthKey();

  if (loading) {
    return <LoadingState label="Chargement des bonus..." />;
  }

  return (
    <PageShell data-testid="bonuses-page">
      <PageHeader title="Mes bonus" description="Consultez vos bonus et les paiements mensuels programmes par la cloture administrative" />

      <div className="grid gap-4 lg:grid-cols-4">
        <Card className="border-[#E6E4E0] bg-gradient-to-br from-[#2A4B46] to-[#1f3a36] text-white shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-2xl font-bold font-['Outfit']">Solde du mois {selectedMonth}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-extrabold sm:text-5xl" data-testid="total-bonuses">
              {formatCurrency(summary?.total || 0)}
            </p>
            <p className="mt-2 text-white/80">
              {currentMonth ? "Le mois courant repart de zero et se remplit avec les bonus du mois." : "Archive mensuelle conservee pour consultation."}
            </p>
          </CardContent>
        </Card>
        <SmallStat title="Paiement programme" value={formatCurrency(selectedArchive?.programmed_payment_mc || 0)} icon={Wallet} />
        <SmallStat title="Paiement confirme" value={formatCurrency(selectedArchive?.paid_mc || 0)} icon={Clock} />
      </div>

      <Card className="border-[#E6E4E0] bg-[#F8F5EF] shadow-sm">
        <CardContent className="p-4 text-sm text-[#5C625F]">
          Les bonus du mois en cours restent visibles dans l'historique. A la fin du mois, l'administration cloture la periode, archive les transactions, puis le système programme automatiquement les paiements bonus du mois.
          Montant déjà programme ou en traitement: <strong className="text-[#2A4B46]">{formatCurrency(withdrawalSummary?.reserved_mc || 0)}</strong>.
        </CardContent>
      </Card>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Archives mensuelles</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 xl:grid-cols-4">
          {archives.map((archive) => (
            <Button
              key={archive.month}
              type="button"
              variant={selectedMonth === archive.month ? "default" : "outline"}
              className={`h-auto justify-start p-4 text-left ${selectedMonth === archive.month ? "bg-[#2A4B46] hover:bg-[#1f3a36]" : ""}`}
              onClick={() => setSelectedMonth(archive.month)}
            >
              <span className="flex w-full items-start gap-3">
                <Archive size={18} className="mt-1 shrink-0" />
                <span className="min-w-0">
                  <span className="block font-semibold">{archive.month}</span>
                  <span className="block text-xs opacity-80">
                    {formatCurrency(archive.total_bonus_mc || 0)} - {archive.is_closed ? "cloture" : "ouvert"}
                  </span>
                </span>
              </span>
            </Button>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Paiement automatique mensuel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-[#5C625F]">
            <p>Le partenaire ne créé plus de demande de retrait manuelle.</p>
            <p>A chaque cloture mensuelle, MMJ archive les transactions, identifie les partenaires actifs PAE/PAR du mois et programme automatiquement les bonus payables.</p>
            <p>Les informations de paiement proviennent du KYC validé. Gardez votre KYC bancaire ou Mobile Money a jour.</p>
          </CardContent>
        </Card>

        <Card className="border-[#E6E4E0] shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Mes paiements mensuels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {withdrawals.length === 0 ? (
              <p className="rounded-xl bg-[#F8F5EF] p-4 text-sm text-[#5C625F]">Aucun paiement mensuel programme pour le moment.</p>
            ) : (
              withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="rounded-xl border border-[#E6E4E0] bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-[#2A4B46]">{withdrawal.id}</p>
                      <p className="text-sm text-[#5C625F]">{formatDate(withdrawal.created_at)}</p>
                    </div>
                    <Badge className={STATUS_BADGES[withdrawal.status]?.className || "bg-slate-100 text-slate-700"}>
                      {STATUS_BADGES[withdrawal.status]?.label || withdrawal.status}
                    </Badge>
                  </div>
                  <p className="mt-3 text-2xl font-bold text-[#2A4B46]">{formatCurrency(withdrawal.reserved_amount_mc || 0)}</p>
                  <p className="text-sm text-[#5C625F]">{withdrawal.payment?.method} {withdrawal.payment?.provider || ""}</p>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Repartition par type de bonus</CardTitle>
        </CardHeader>
        <CardContent>
          {bonusTypeEntries.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {bonusTypeEntries.map(([bonusType, amount]) => (
                <div key={bonusType} className="rounded-2xl bg-[#F3EFE9] p-4" data-testid={`bonus-type-${bonusType}`}>
                  <p className="text-sm text-[#5C625F] mb-1">{BONUS_TYPES[bonusType]?.label || bonusType}</p>
                  <p className="text-2xl font-bold text-[#2A4B46]">{formatCurrency(amount)}</p>
                  <Badge className="bg-[#D4A373] text-white mt-2">{BONUS_TYPES[bonusType]?.rate}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-[#5C625F] py-8">Aucun bonus enregistre</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-[#E6E4E0] shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl font-semibold text-[#2A4B46] font-['Outfit']">Historique detaille</CardTitle>
        </CardHeader>
        <CardContent>
          {bonuses.length === 0 ? (
            <p className="text-center text-[#5C625F] py-8">Aucun bonus dans l'historique</p>
          ) : (
            <div className="space-y-3">
              {bonuses.map((bonus) => (
                <div key={bonus.id} className="flex flex-col gap-3 rounded-xl border border-[#E6E4E0] bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`bonus-row-${bonus.id}`}>
                  <div>
                    <Badge className="bg-[#F3EFE9] text-[#2A4B46]">{BONUS_TYPES[bonus.bonus_type]?.label || bonus.bonus_type}</Badge>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#5C625F]">
                      <span>{formatDate(bonus.created_at)}</span>
                      <span>{bonus.month}</span>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-[#2A4B46]">{formatCurrency(bonus.amount_mc)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}

function SmallStat({ title, value, icon: Icon }) {
  return (
    <Card className="border-[#E6E4E0] shadow-sm">
      <CardContent className="flex h-full items-center justify-between gap-4 p-5">
        <div>
          <p className="text-sm text-[#5C625F]">{title}</p>
          <p className="mt-2 text-2xl font-bold text-[#2A4B46]">{value}</p>
        </div>
        <span className="rounded-full bg-[#F3EFE9] p-3 text-[#2A4B46]">
          <Icon size={22} />
        </span>
      </CardContent>
    </Card>
  );
}

function getCurrentMonthKey() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
